import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, money } from "@/lib/api";
import { DEFAULT_PACKAGES } from "@/lib/packages";
import { toast } from "sonner";
import { useSeo } from "@/lib/seo";
import { getMeta } from "@/seo/pages";
import { SOFTWARE } from "@/seo/homeJsonld";
import { trackPage, getAttribution } from "@/lib/track";
import { AccountMenu } from "@/components/account";
import "@/styles/home.css";

const SITE = "https://99tests.com";

// Verbatim from the homepage "Before you pay" section — copy is final.
const FAQ = [
  ["What formats do you accept for test cases?", "CSV, Excel, Google Sheets export, Word, Markdown, plain text, and TestRail or Jira exports. AI-generated cases from any tool are fine. We parse them into a plan and show you the coverage before testers start."],
  ["How do I share the build?", "An APK download link, a Google Play internal testing link, a TestFlight public link, or for web apps a staging or production URL. If it needs test credentials or basic auth, include them in the submission form; they are shown only to assigned testers."],
  ["Which browsers and laptops do you cover?", "Chrome, Safari, Edge and Firefox on Windows, macOS and ChromeOS, on testers' own laptops and desktops. You choose the browser and OS as targets the same way you choose a phone. The session records the actual browser version and screen size, and the report shows them."],
  ["Who chooses the devices?", "You set targets — brand and minimum OS for phones, browser and OS for laptops. We match testers who own those devices. If a match can't be found within your turnaround we widen once, and if it still can't be filled the unfilled sessions are credited back to your email and applied to your next job."],
  ["Is my app confidential?", "Testers accept confidentiality terms before they can take sessions. Builds are shared by link only to assigned testers and are not stored by 99tests beyond the job. Recordings are stored privately and linked only from your report."],
  ["Do you invoice with GST?", "Yes, for Indian customers. Enter your GSTIN at checkout and Stripe issues a compliant invoice immediately. Customers outside India are billed in US dollars and receive a standard invoice."],
  ["What if there are more test cases than the package covers?", "After upload you'll see exactly which cases are uncovered and can add five sessions at a time. Or leave them; the report lists them as not run."],
  ["Can I get a custom scope?", "Not yet. The three packages cover most release checks, and blocks of five sessions cover the rest."],
  ["What if the report is late?", "If we miss the report deadline shown on your invoice, get in touch and we'll make it right. The only exception is when your build or test credentials weren't available to our testers."],
  ["Does 99tests charge per slot or by the hour?", "Per slot, fixed. A slot is one phone, laptop or browser; each package bundles a set number of slots with no hourly billing, no overage and no expenses. Need more? Add slots in blocks of five."],
  ["Which package gets me a report in 3 business days?", "Starter ($129 / ₹9,999) and Standard ($249 / ₹18,999) both deliver a verified report in 3 business days. The larger Deep package ($449 / ₹34,999), with 90-minute sessions, takes 5. Most teams start with Standard."],
];

const kickers = { starter: "One flow, two phones.", standard: "A release candidate across your main devices.", deep: "Longer exploratory sessions, wider matrix." };
const cases = { starter: "up to ~25", standard: "up to ~50", deep: "up to ~120" };
const devNote = { starter: "your choice", standard: "mix freely", deep: "mix freely" };

function Header() {
  return (
    <header className="site-header"><div className="wrap">
      <a className="logo" href="/"><i></i>99tests</a>
      <nav>
        <a href="/#how">How it works</a>
        <a href="/devices" data-testid="nav-devices">Devices</a>
        <a href="/pricing">Pricing</a>
        <a href="/about" data-testid="nav-about">About</a>
        <a href="https://blog.99tests.com/" data-testid="nav-blog">Blog</a>
        <a href="#faq">Questions</a>
        <AccountMenu />
        <a className="btn sm blue" href="#packages" data-testid="pricing-start-job-btn">Start a job</a>
      </nav>
    </div></header>
  );
}

function Footer() {
  return (
    <footer className="site-footer"><div className="wrap">
      <div>© 2026 99tests · Bengaluru<br /><span className="small">Not test automation. Not a certification or an audit under any standard.</span></div>
      <div data-testid="pricing-footer-explore">
        <a href="/real-device-testing-india">Real-device testing in India</a>
        <a href="/test-ai-generated-test-cases">Test AI-generated test cases</a>
        <a href="/vs-device-clouds-and-automation">vs Device clouds &amp; automation</a>
      </div>
      <div>
        <a href="/crowdsourced-testing">Crowdsourced testing</a>
        <a href="/">Home</a>
        <a href="/sample-report/">Sample report</a>
        <a href="https://blog.99tests.com/" data-testid="footer-blog">Blog</a>
        <a href="/join">Become a tester</a>
        <a href="/mcp-server" data-testid="pricing-footer-mcp">For AI agents (MCP)</a>
        <a href="/terms">Client terms</a>
        <a href="mailto:hello@99tests.com">hello@99tests.com</a>
      </div>
    </div></footer>
  );
}

export default function Pricing() {
  const path = "/pricing";
  const jsonld = {
    "@context": "https://schema.org",
    "@graph": [
      { "@type": "BreadcrumbList", itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE + "/" },
        { "@type": "ListItem", position: 2, name: "Pricing", item: SITE + path },
      ] },
      SOFTWARE,
      { "@type": "Product", name: "99tests real-device testing", brand: { "@type": "Brand", name: "99tests" },
        description: "Fixed-price real-device testing on phones, laptops and browsers. From $129, or ₹9,999 + GST in India. Verified report in 3 business days.",
        offers: [
          { "@type": "Offer", name: "Starter", price: "9999", priceCurrency: "INR", url: SITE + "/pricing", availability: "https://schema.org/InStock" },
          { "@type": "Offer", name: "Standard", price: "18999", priceCurrency: "INR", url: SITE + "/pricing", availability: "https://schema.org/InStock" },
          { "@type": "Offer", name: "Deep", price: "34999", priceCurrency: "INR", url: SITE + "/pricing", availability: "https://schema.org/InStock" },
          { "@type": "Offer", name: "Starter", price: "129", priceCurrency: "USD", url: SITE + "/pricing", availability: "https://schema.org/InStock" },
          { "@type": "Offer", name: "Standard", price: "249", priceCurrency: "USD", url: SITE + "/pricing", availability: "https://schema.org/InStock" },
          { "@type": "Offer", name: "Deep", price: "449", priceCurrency: "USD", url: SITE + "/pricing", availability: "https://schema.org/InStock" },
        ],
      },
      { "@type": "FAQPage", mainEntity: FAQ.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })) },
    ],
  };
  useSeo({
    ...getMeta(path),
    path,
    jsonld,
  });
  useEffect(() => { trackPage(path); }, []);

  const { data } = useQuery({ queryKey: ["config"], queryFn: api.config });
  const [cur, setCur] = useState("INR");
  const [loading, setLoading] = useState(null);
  const [pending, setPending] = useState(null);
  const [email, setEmail] = useState(() => { try { return localStorage.getItem("99t_email") || ""; } catch (e) { return ""; } });
  const [credit, setCredit] = useState(null);

  useEffect(() => {
    if (typeof navigator !== "undefined" && navigator.userAgent.includes("ReactSnap")) return;
    try {
      const saved = localStorage.getItem("99t_cur");
      if (saved === "INR" || saved === "USD") { setCur(saved); return; }
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
      if (!(tz === "Asia/Kolkata" || tz === "Asia/Calcutta")) setCur("USD");
    } catch (e) {}
  }, []);

  function pickCur(c) { setCur(c); try { localStorage.setItem("99t_cur", c); } catch (e) {} }

  useEffect(() => {
    if (!pending || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setCredit(null); return; }
    const t = setTimeout(() => api.creditBalance(email, cur).then((r) => setCredit(r.balance_minor)).catch(() => setCredit(null)), 300);
    return () => clearTimeout(t);
  }, [email, pending, cur]);

  function buy(pkg) { setPending(pkg); }

  async function confirmBuy() {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { toast.error("Enter the email for your invoice and report"); return; }
    setLoading(pending);
    try {
      try { localStorage.setItem("99t_email", email); } catch (e) {}
      const attr = getAttribution() || {};
      const res = await api.checkout({ package: pending, cur: cur.toLowerCase(), email,
        country: cur === "INR" ? "IN" : "US", origin_url: window.location.origin,
        landing: attr.landing || "/pricing", source: attr.source || null,
        medium: attr.medium || null, campaign: attr.campaign || null });
      if (res.paid_by_credit) toast.success("Covered by your credit — no payment needed");
      window.location.href = res.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setLoading(null);
    }
  }

  const packages = data?.packages || DEFAULT_PACKAGES;
  const order = ["starter", "standard", "deep"];

  return (
    <>
      <Header />
      <main data-testid="pricing-page">
        <section className="hero">
          <div className="wrap">
            <div>
              <div className="ref"><b>Fixed price</b><span className="dot"></span><b>3 business days</b><span className="dot"></span><span>Phones · laptops · browsers</span></div>
              <h1>Fixed-price real-device testing.<br /><em>Nothing further is billed.</em></h1>
              <p className="lede">No hourly overage, no expenses, no change requests. A slot is a phone, a laptop or a browser — mix them in one job. Add sessions in blocks of five if you need more. Indian customers billed in INR with GST; everyone else in USD.</p>
              <div className="actions">
                <a className="btn blue" href="#packages">See packages</a>
                <a className="btn ghost" href="/sample-report/" data-testid="pricing-sample-report-link">Read a sample report</a>
              </div>
              <p className="muted small" data-testid="pricing-mcp-line" style={{ marginTop: 14 }}>Works with Claude Code and Cursor via MCP — <a href="/mcp-server">connect your agent</a>.</p>
            </div>
          </div>
        </section>

        <section style={{ paddingTop: 0, borderTop: 0 }}>
          <div className="wrap">
            <div className="narrow" data-testid="pricing-answer" style={{ marginLeft: 0, background: "var(--band,#f6f7fb)", border: "1px solid var(--line,#e6e8f0)", borderRadius: 12, padding: "18px 20px" }}>
              <p className="lede" style={{ margin: 0 }}>99tests real-device testing is fixed-price: <b>Starter $129 (₹9,999)</b>, <b>Standard $249 (₹18,999)</b> and <b>Deep $449 (₹34,999)</b> — each a flat fee with no hourly billing, no overage and no account. Starter and Standard deliver a verified report in 3 business days; Deep in 5. Indian customers are billed in INR + GST, everyone else in USD.</p>
            </div>
          </div>
        </section>

        <section className="band" id="packages">
          <div className="wrap">
            <div className="packhead">
              <div>
                <span className="eyebrow">Pricing</span>
                <h2 style={{ marginBottom: 6 }}>Three packages. One fixed price each.</h2>
                <p className="lede" style={{ margin: 0 }}>Choose a package, pay by card, and testers are on it within the hour. The invoice is issued at checkout.</p>
              </div>
              <div className="cur" role="group" aria-label="Currency">
                {["INR", "USD"].map((c) => (
                  <button key={c} type="button" data-testid={`pricing-cur-${c}`} aria-pressed={cur === c} onClick={() => pickCur(c)}>
                    {c === "INR" ? "₹ INR" : "$ USD"}
                  </button>
                ))}
              </div>
            </div>
            <div className="packs">
              {order.map((key) => {
                const p = packages[key];
                if (!p) return null;
                const primary = key === "standard";
                return (
                  <div key={key} className={`pack ${primary ? "primary" : ""}`} data-testid={`pricing-pack-${key}`}>
                    {primary && <span className="pill blue badge">Most teams start here</span>}
                    <h3>{p.display_name}</h3>
                    <p className={`kicker ${primary ? "muted" : ""}`}>{kickers[key]}</p>
                    <div className="price">{money(p.prices[cur].display_minor, cur)}<small className="tax">{cur === "INR" ? "+ GST" : "USD"}</small></div>
                    <ul>
                      <li>Sessions <span>{p.sessions} × {p.band === "DEEP" ? "90" : "45"} min</span></li>
                      <li>Devices or browsers <span>{p.devices}, {devNote[key]}</span></li>
                      <li>Test cases <span>{cases[key]}</span></li>
                      <li>Report <span>{p.turnaround_days} business days</span></li>
                    </ul>
                    <button className="btn block" data-testid={`pricing-buy-${key}`} disabled={loading === key} onClick={() => buy(key)}>
                      {loading === key ? "Starting checkout…" : `Pay for ${p.display_name}`}
                    </button>
                  </div>
                );
              })}
            </div>
            <div className="terms">
              <div><b>Unfilled sessions credited</b><p>If a device target can't be matched within your turnaround, the sessions that didn't run are credited back automatically and applied to your next job or re-run.</p></div>
              <div><b>Paid before work starts</b><p>Because the job is automated end to end, there is no invoice cycle. Indian customers are billed in INR with GST; everyone else in USD.</p></div>
              <div><b>Below most approval thresholds</b><p>Starter and Standard sit under the card limit of most engineering managers. That is deliberate: no procurement.</p></div>
            </div>
            <p className="muted" data-testid="pricing-tools-note" style={{ marginTop: 16 }}>Not sure which package fits? Count your cases in the <a href="/tools/test-plan-builder">test plan builder</a> or use the <a href="/tools/qa-cost-calculator">QA cost calculator</a>.</p>
          </div>
        </section>

        <section id="faq">
          <div className="wrap narrow" style={{ marginLeft: 0 }}>
            <div className="section-head"><span className="eyebrow">Questions</span><h2>Before you pay.</h2></div>
            <div className="faqbox" data-testid="pricing-faq">
              {FAQ.map(([q, a], i) => (
                <details className="f" key={i} open={i === 0}><summary>{q}</summary><p>{a}</p></details>
              ))}
            </div>
          </div>
        </section>

        <section style={{ borderTop: 0 }}>
          <div className="wrap">
            <div className="cta">
              <div>
                <h2>Ship this release knowing it works on a Redmi — and on Safari.</h2>
                <p>Pay, upload, and testers are on it within the hour. Report in three business days.</p>
              </div>
              <a className="btn" href="#packages">See packages</a>
            </div>
          </div>
        </section>
      </main>
      {pending && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(20,30,70,.5)", zIndex: 60, display: "grid", placeItems: "center", padding: 20 }} onClick={() => !loading && setPending(null)} data-testid="pricing-checkout-modal">
          <div className="panel" style={{ background: "var(--paper)", borderRadius: "var(--r-l)", padding: 28, width: "100%", maxWidth: 460, boxShadow: "var(--shadow)" }} onClick={(e) => e.stopPropagation()}>
            <span className="eyebrow">Almost there</span>
            <h3 style={{ marginBottom: 6 }}>Pay for {packages[pending]?.display_name}</h3>
            <p className="muted small" style={{ margin: "0 0 14px" }}>We send the invoice, status link and report to this email. Any credit from unfilled sessions on this email is applied automatically.</p>
            <div className="field"><label htmlFor="co-email">Your email</label>
              <input id="co-email" type="email" autoFocus data-testid="pricing-checkout-email" placeholder="you@company.com" value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && confirmBuy()} /></div>
            {credit > 0 && (
              <div className="small" style={{ background: "rgba(14,124,75,.08)", border: "1px solid rgba(14,124,75,.3)", borderRadius: 8, padding: "10px 12px", marginBottom: 12 }} data-testid="pricing-checkout-credit">
                Credit available: <b>{money(credit, cur)}</b>. You'll pay <b>{money(Math.max(0, (packages[pending]?.prices?.[cur]?.display_minor || 0) - credit), cur)}</b>{(packages[pending]?.prices?.[cur]?.display_minor || 0) - credit <= 0 ? " — fully covered, no card needed." : "."}
              </div>
            )}
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button type="button" className="btn ghost" disabled={!!loading} onClick={() => setPending(null)} data-testid="pricing-checkout-cancel">Cancel</button>
              <button type="button" className="btn blue" disabled={!!loading} onClick={confirmBuy} data-testid="pricing-checkout-continue">{loading ? "Starting checkout…" : credit > 0 && (packages[pending]?.prices?.[cur]?.display_minor || 0) - credit <= 0 ? "Use credit and start" : "Continue to payment"}</button>
            </div>
          </div>
        </div>
      )}
      <Footer />
    </>
  );
}
